import { Button } from '@/components/ui/button';
import { useAppRegion, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import { getLatestAppVersion, useOfflineAppDownloadUrl } from '@/lib/offline/OfflineAppHelper';
import { DownloadLinear } from '@assessprep/icons/solar';
import { useTranslate } from '@refinedev/core';
import {
  instructionsHeadingClass,
  instructionsListClass,
  sectionClass,
  supportedVersionsClass,
} from './sectionStyles';

const PrerequisiteLink = ({ href }: { href: string }) => (
  <a
    className="text-primary underline underline-offset-4"
    href={href}
    target="_blank"
    rel="noreferrer"
  >
    {href}
  </a>
);

export const WindowsTab = () => {
  const translate = useTranslate();
  const appRegion = useAppRegion();
  const enabledFeatures = useEnabledFeatures();
  const sebAppLatestVersion = getLatestAppVersion('seb');
  const win10DownloadUrl = useOfflineAppDownloadUrl('win10', appRegion, enabledFeatures);

  return (
    <div className="space-y-6">
      <div className={sectionClass}>
        <Button asChild data-testid="download-win-link">
          <a href={win10DownloadUrl ?? undefined}>
            <DownloadLinear aria-hidden />
            {translate('OfflineApp.download_seb_setup_bundle')} {sebAppLatestVersion}
          </a>
        </Button>
        <p className={supportedVersionsClass}>
          {translate('OfflineApp.supported_versions_windows_10_and_11')}
        </p>
      </div>

      <div className={sectionClass}>
        <h3 className={instructionsHeadingClass}>
          {translate('OfflineApp.instructions_to_install_the_app_on_windows')}
        </h3>
        <ol className={instructionsListClass}>
          <li>{translate('OfflineApp.download_the_setup_file_using_the_button_above')}</li>
          <li>{translate('OfflineApp.run_the_setup_file_and_follow_the_instructions_in_case')}</li>
        </ol>
      </div>

      <div className={sectionClass}>
        <h3 className={instructionsHeadingClass}>
          {translate('OfflineApp.troubleshoot_installation_problems')}
        </h3>
        <ol className={instructionsListClass}>
          <li>{translate('OfflineApp.in_case_you_are_facing_issues_setup_failed_error_while')}</li>
        </ol>
      </div>

      <div className={sectionClass}>
        <h3 className={instructionsHeadingClass}>{translate('OfflineApp.prerequisites')}:</h3>
        <ol className={instructionsListClass}>
          <li>
            {translate('OfflineApp.net_framework_481_runtime')}:{' '}
            <PrerequisiteLink href="https://dotnet.microsoft.com/download/dotnet-framework/net48" />
          </li>
          <li>
            {translate('OfflineApp.visual_c_20152019_redistributable')}:{' '}
            <PrerequisiteLink href="https://learn.microsoft.com/en-us/cpp/windows/latest-supported-vc-redist" />
          </li>
        </ol>
        <p className={supportedVersionsClass}>
          {translate('OfflineApp.note_these_are_automatically_installed_with_the_setup_bundle_and')}
        </p>
      </div>

      <div className={sectionClass}>
        <h3 className={instructionsHeadingClass}>{translate('OfflineApp.msi_package')}:</h3>
        <Button variant="outline" asChild data-testid="download-msi-link">
          <a
            href={
              appRegion === 'china'
                ? 'https://production-eks-assets.assessprep.cn/SEB_setup/AP_SEB_3.10.1.9999_x64.msi'
                : 'https://v2res1.assessprep.com/manual_uploads/SEB%20Win/AP_SEB_3.10.1.9999.msi'
            }
          >
            <DownloadLinear aria-hidden />
            {translate('OfflineApp.msi_package')}
          </a>
        </Button>
      </div>

      <p className="text-sm">
        <strong className="font-medium">
          {translate('OfflineApp.important_please_uninstall_previous_version')}
        </strong>
        <br />
        {translate(
          'OfflineApp.after_installing_the_latest_version_please_manually_uninstall_any_old'
        )}
      </p>
    </div>
  );
};
